import { generateUUID } from '../utils/uuid';
import { useState } from 'react';
import type {
  CodingSuggestion,
  GapType,
  SuggestionStatus,
  Evidence,
  DocumentType,
} from '../types';

interface Props {
  suggestion?: CodingSuggestion;
  onSave: (data: Omit<CodingSuggestion, 'id'>) => void;
  onClose: () => void;
}

const GAP_TYPES: { value: GapType; label: string }[] = [
  { value: 'suspect_new', label: 'Suspecting Code Gap' },
  { value: 'opportunity', label: 'Recaptured Opportunity' },
  { value: 'recapture', label: 'Recaptured Existing' },
  { value: 'recapture_stale', label: 'Suspecting Emerging' },
  { value: 'over_claimed', label: 'Recaptured Risk' },
];

const STATUSES: { value: SuggestionStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];


const DOC_TYPES: { value: DocumentType; label: string }[] = [
  { value: 'progress_note', label: 'Progress Note' },
  { value: 'lab_result', label: 'Lab Result' },
  { value: 'consult', label: 'Consult Note' },
  { value: 'medication_list', label: 'Medication List' },
  { value: 'procedure_report', label: 'Procedure Report' },
  { value: 'vital_signs', label: 'Vital Signs' },
  { value: 'imaging', label: 'Imaging' },
  { value: 'discharge_summary', label: 'Discharge Summary' },
  { value: 'other', label: 'Other' },
];

interface EvidenceForm {
  sourceDocument: string;
  documentType: DocumentType;
  excerpt: string;
  keyFindings: string;
}

function toEvidenceForm(ev: Evidence): EvidenceForm {
  return {
    sourceDocument: ev.sourceDocument,
    documentType: ev.documentType,
    excerpt: ev.excerpt,
    keyFindings: ev.keyFindings.join('\n'),
  };
}

function fromEvidenceForm(form: EvidenceForm): Evidence {
  return {
    id: generateUUID(),
    sourceDocument: form.sourceDocument,
    documentType: form.documentType,
    documentDate: new Date().toISOString().split('T')[0],
    excerpt: form.excerpt,
    highlights: [],
    relevanceScore: 0.85,
    keyFindings: form.keyFindings.split('\n').map(s => s.trim()).filter(Boolean),
  };
}

const EMPTY_EVIDENCE: EvidenceForm = {
  sourceDocument: '',
  documentType: 'progress_note',
  excerpt: '',
  keyFindings: '',
};

export function SuggestionFormModal({ suggestion, onSave, onClose }: Props) {
  const isEdit = !!suggestion;

  const [icdCode, setIcdCode] = useState(suggestion?.icdCode ?? '');
  const [icdDescription, setIcdDescription] = useState(suggestion?.icdDescription ?? '');
  const [gapType, setGapType] = useState<GapType>(suggestion?.gapType ?? 'suspect_new');
  const [status, setStatus] = useState<SuggestionStatus>(suggestion?.status ?? 'pending');
  const [hccCategory, setHccCategory] = useState(suggestion?.hccCategory ?? '');
  const [hccDescription, setHccDescription] = useState(suggestion?.hccDescription ?? '');
  const [hccWeight] = useState(suggestion?.hccWeight ?? 0);
  const [justification, setJustification] = useState(suggestion?.justification ?? '');
  const [clinicalReasoning, setClinicalReasoning] = useState(suggestion?.clinicalReasoning ?? '');
  const [evidenceList, setEvidenceList] = useState<EvidenceForm[]>(
    suggestion?.evidence?.length ? suggestion.evidence.map(toEvidenceForm) : [{ ...EMPTY_EVIDENCE }],
  );

  const handleSave = () => {
    if (!icdCode.trim() || !icdDescription.trim()) return;

    const result: Omit<CodingSuggestion, 'id'> = {
      icdCode: icdCode.trim(),
      icdDescription: icdDescription.trim(),
      hccCategory: hccCategory.trim() || null,
      hccDescription: hccDescription.trim() || null,
      hccWeight,
      status,
      gapType,
      lastCapturedDate: suggestion?.lastCapturedDate ?? null,
      justification: justification.trim(),
      clinicalReasoning: clinicalReasoning.trim(),
      evidence: evidenceList
        .filter(e => e.sourceDocument.trim() || e.excerpt.trim())
        .map(fromEvidenceForm),
    };

    if (status === 'accepted') {
      result.acceptedAt = suggestion?.acceptedAt ?? new Date().toISOString();
      result.acceptedBy = suggestion?.acceptedBy ?? 'Provider';
    } else if (status === 'rejected') {
      result.rejectedAt = suggestion?.rejectedAt ?? new Date().toISOString();
      result.rejectedBy = suggestion?.rejectedBy ?? 'Provider';
      result.rejectionReason = suggestion?.rejectionReason ?? 'Edited';
    }

    onSave(result);
    onClose();
  };

  const addEvidence = () => setEvidenceList(prev => [...prev, { ...EMPTY_EVIDENCE }]);
  const removeEvidence = (idx: number) => setEvidenceList(prev => prev.filter((_, i) => i !== idx));
  const updateEvidence = (idx: number, field: keyof EvidenceForm, value: string) =>
    setEvidenceList(prev => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[5vh] bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between z-10">
          <h2 className="text-sm font-bold text-gray-800">
            {isEdit ? 'Edit Suggestion' : 'Add New Code'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* ICD Code + Description */}
          <div className="grid grid-cols-[140px_1fr] gap-3">
            <div>
              <label className="block text-2xs font-semibold text-gray-500 uppercase mb-1">ICD Code *</label>
              <input
                value={icdCode}
                onChange={e => setIcdCode(e.target.value)}
                placeholder="E11.9"
                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md font-mono focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-2xs font-semibold text-gray-500 uppercase mb-1">Description *</label>
              <input
                value={icdDescription}
                onChange={e => setIcdDescription(e.target.value)}
                placeholder="Type 2 diabetes mellitus without complications"
                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Gap Type + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-2xs font-semibold text-gray-500 uppercase mb-1">Gap Type</label>
              <select
                value={gapType}
                onChange={e => setGapType(e.target.value as GapType)}
                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              >
                {GAP_TYPES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-2xs font-semibold text-gray-500 uppercase mb-1">Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value as SuggestionStatus)}
                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              >
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* HCC fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-2xs font-semibold text-gray-500 uppercase mb-1">HCC Category</label>
              <input
                value={hccCategory}
                onChange={e => setHccCategory(e.target.value)}
                placeholder="HCC 19"
                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-2xs font-semibold text-gray-500 uppercase mb-1">HCC Description</label>
              <input
                value={hccDescription}
                onChange={e => setHccDescription(e.target.value)}
                placeholder="Diabetes without complication"
                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Justification */}
          <div>
            <label className="block text-2xs font-semibold text-gray-500 uppercase mb-1">Justification</label>
            <textarea
              value={justification}
              onChange={e => setJustification(e.target.value)}
              rows={2}
              placeholder="Concise 2-3 sentence reason for this code assignment..."
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md resize-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
            />
          </div>

          {/* Clinical Reasoning */}
          <div>
            <label className="block text-2xs font-semibold text-gray-500 uppercase mb-1">Clinical Reasoning</label>
            <textarea
              value={clinicalReasoning}
              onChange={e => setClinicalReasoning(e.target.value)}
              rows={2}
              placeholder="Describe the clinical rationale..."
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md resize-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
            />
          </div>

          {/* Evidence */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-2xs font-semibold text-gray-500 uppercase">Evidence</label>
              <button
                onClick={addEvidence}
                className="text-2xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <span className="text-sm leading-none">+</span> Add Evidence
              </button>
            </div>
            <div className="space-y-3">
              {evidenceList.map((ev, idx) => (
                <div key={idx} className="border border-gray-200 rounded-md p-3 bg-gray-50 relative">
                  {evidenceList.length > 1 && (
                    <button
                      onClick={() => removeEvidence(idx)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-500 text-xs"
                    >&times;</button>
                  )}
                  <div className="grid grid-cols-[1fr_140px] gap-2 mb-2">
                    <input
                      value={ev.sourceDocument}
                      onChange={e => updateEvidence(idx, 'sourceDocument', e.target.value)}
                      placeholder="Source document name"
                      className="px-2 py-1 text-xs border border-gray-200 rounded focus:border-blue-400 focus:outline-none"
                    />
                    <select
                      value={ev.documentType}
                      onChange={e => updateEvidence(idx, 'documentType', e.target.value)}
                      className="px-2 py-1 text-xs border border-gray-200 rounded focus:border-blue-400 focus:outline-none"
                    >
                      {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                  <textarea
                    value={ev.excerpt}
                    onChange={e => updateEvidence(idx, 'excerpt', e.target.value)}
                    rows={2}
                    placeholder="Clinical excerpt..."
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded resize-none mb-2 focus:border-blue-400 focus:outline-none"
                  />
                  <textarea
                    value={ev.keyFindings}
                    onChange={e => updateEvidence(idx, 'keyFindings', e.target.value)}
                    rows={2}
                    placeholder="Key findings (one per line)"
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded resize-none focus:border-blue-400 focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 py-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!icdCode.trim() || !icdDescription.trim()}
            className="px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isEdit ? 'Save Changes' : 'Add Code'}
          </button>
        </div>
      </div>
    </div>
  );
}
